import { useState, useEffect, useRef } from 'react';

import { isWiseAlias } from '../../resources/aliasFilter';
import extractQuote from '../../resources/clients/quotes/extractQuote';
import fetchQuotes from '../../resources/clients/quotes/fetchQuotes';

export const STATUS = {
  PENDING: 'pending',
  RESOLVED: 'resolved',
  REJECTED: 'rejected',
};

const DEFAULT_STATE = {
  status: STATUS.PENDING,
  providers: [],
  midMarketRate: 0,
  twSavings: 0,
};

const API_ERROR = {
  status: STATUS.REJECTED,
  errorType: 'EXCEPTION',
};

const TW_DELIVERY_FEE_GBP = 7;
const TW_DELIVERY_ESTIMATE_UK = {
  min: 'P2D',
  max: 'P6D',
};

const getBestNonTwFee = (providers) =>
  providers.reduce((lowestFee, current) => {
    if (isWiseAlias(current.alias)) {
      return lowestFee;
    }
    return Math.min(lowestFee, current.fee);
  }, Infinity);

// Get the best (highest) received amount from non-Wise providers
// Higher received amount = better deal for the customer
const getBestNonTwReceivedAmount = (providers) =>
  providers.reduce((highestReceived, current) => {
    if (isWiseAlias(current.alias)) {
      return highestReceived;
    }
    return Math.max(highestReceived, current.receivedAmount || 0);
  }, 0);

const calculateRateMarkupFee = ({ rate }, sendAmount, midMarketRate) => {
  // calculate receivedAmount at midmarket rate
  const midMarketConversion = sendAmount * midMarketRate;
  // calculate receivedAmount at provider rate
  const providerConversion = sendAmount * rate;
  // take difference, get amount in targetCurrency
  const diff = midMarketConversion - providerConversion;
  // use midmarket rate to convert back to sourceCurrency
  return diff / midMarketRate;
};

// To reach MVP, some there were some changes made to the designed API contract
// These should be removed eventually, so this is a safe way to remap API responses
// if it detects an MVP API response
const temporaryProviderRemapping = (sendAmount, midMarketRate) => (provider) => {
  if (provider.feeBreakdown) {
    return provider;
  }
  if (isWiseAlias(provider.alias)) {
    const conversionFee = provider.fee - TW_DELIVERY_FEE_GBP; // fee includes delivery
    return {
      ...provider,
      feeBreakdown: [
        {
          feeType: 'DELIVERY',
          feeAmount: TW_DELIVERY_FEE_GBP,
        },
        {
          feeType: 'CONVERSION',
          feeAmount: conversionFee,
        },
      ],
      deliveryEstimation: {
        duration: TW_DELIVERY_ESTIMATE_UK,
        providerGivesEstimate: true,
      },
    };
  }
  const rateMarkupFee = calculateRateMarkupFee(provider, sendAmount, midMarketRate);
  return {
    ...provider,
    fee: provider.fee + rateMarkupFee,
    feeBreakdown: [
      {
        feeType: 'DELIVERY',
        feeAmount: provider.fee,
      },
      {
        feeType: 'MARKUP',
        feeAmount: rateMarkupFee,
      },
    ],
  };
};

const collapseQuotes = (targetCurrency) => (provider) => {
  const { quotes, ...rest } = provider;
  return {
    ...rest,
    ...extractQuote(quotes, targetCurrency),
  };
};

const transformProviders = (sendAmount, rawProviders, targetCurrency, midMarketRate) =>
  rawProviders?.length
    ? rawProviders
        .map(collapseQuotes(targetCurrency))
        .map(temporaryProviderRemapping(sendAmount, midMarketRate))
        .sort((a, b) => b.receivedAmount - a.receivedAmount)
    : [];

const getMidMarketRateFromProviders = (providers) => {
  const twQuote = providers.find((provider) => isWiseAlias(provider.alias))?.quotes[0];
  return twQuote?.rate;
};

const transformResponse = (raw) => {
  if (!raw.providers.length) {
    return {
      midMarketRate: 0,
      providers: [],
      twSavings: 0,
    };
  }

  const { targetCurrency } = raw;

  const midMarketRate = getMidMarketRateFromProviders(raw?.providers);
  const providers = transformProviders(
    raw?.sendAmount,
    raw?.providers,
    targetCurrency,
    midMarketRate,
  );
  const bestNonTwReceivedAmount = getBestNonTwReceivedAmount(providers);
  const twQuote = providers.find((provider) => isWiseAlias(provider.alias));

  // Calculate savings based on received amount (what customer actually gets)
  // If Wise gives more money than any competitor, Wise is cheaper
  const twReceivedAmount = twQuote?.receivedAmount || 0;
  const receivedAmountDifference = twReceivedAmount - bestNonTwReceivedAmount;

  // Convert received amount difference back to source currency to get savings
  const twSavings = receivedAmountDifference > 0
    ? Math.abs(receivedAmountDifference / midMarketRate) // Convert to source currency
    : 0; // If Wise doesn't give more money, no savings
  return {
    midMarketRate,
    providers,
    twSavings,
  };
};

const useTravelMoneyApi = (sendAmount, sourceCurrency, targetCurrency, initialData) => {
  let initialState = DEFAULT_STATE;
  if (initialData) {
    try {
      const transformed = transformResponse(initialData);
      initialState = {
        status: STATUS.RESOLVED,
        ...transformed,
      };
    } catch (error) {
      throw new Error(`Error adapting initialData API response: ${error}`);
    }
  }
  const [state, setState] = useState(initialState);
  // Don't re-fetch on mount if initialData provided
  const shouldRunEffect = useRef(!initialData);

  useEffect(() => {
    async function fetchFn() {
      setState({
        ...state,
        status: STATUS.PENDING,
      });
      let result = null;
      let providerTypes = 'travelMoney';
      try {
        result = await fetchQuotes(sourceCurrency, targetCurrency, sendAmount, null, null, {}, providerTypes);
      } catch (error) {
        setState(API_ERROR);
        throw new Error(`Could not travel money API results: ${error}`);
      }

      if (!result.providers.length) {
        setState({
          status: STATUS.REJECTED,
          errorType: 'UNSUPPORTED_ROUTE',
        });
        return;
      }

      let transformed = null;
      try {
        transformed = transformResponse(result);
      } catch (error) {
        setState(API_ERROR);
        throw new Error(`Could not adapt API response: ${error}`);
      }

      setState({
        status: STATUS.RESOLVED,
        ...transformed,
      });
    }

    if (shouldRunEffect.current) {
      fetchFn();
    } else {
      shouldRunEffect.current = true;
    }
  }, [sendAmount, sourceCurrency, targetCurrency]);
  return state;
};

export default useTravelMoneyApi;
